<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { useDialogsStore } from '@/stores/dialogsStore'
import type { AmortizationParams } from '@/stores/dialogsStore'
import { apiFetch } from '@/utils/api'
import { formatCurrency } from '@/utils/format'
import type { SettlementDocument } from '@/types/settlement-documents'

import AmortizationBackToolbar from './AmortizationBackToolbar.vue'

// Select window (multi mode) of the amortization dialog (ADA-4437 etapp 5). Ported from
// @old AmortizationSelect: pick a creditor, tick the debts to include; Next fetches the action
// params for the selection (the gate GET on the first selected document's case id) and stores
// params + selection on the dialog payload.
const emit = defineEmits<{ next: [] }>()

const { t } = useI18n()
const dialogsStore = useDialogsStore()

const selectedCreditor = ref<string | null>(null)
const selectedDebts = ref<string[]>([])
const loading = ref(false)

const documents = computed(() => dialogsStore.amortization?.multiple?.documents ?? [])

const creditors = computed(() => {
  const ids = [...new Set(documents.value.map((document) => document.creditor))]
  return ids
    .filter((id): id is string => id != null)
    .map((id) => {
      const debts = documents.value.filter((document) => document.creditor === id)
      return { id, name: `${debts[0]?.creditor_name ?? ''} (${debts.length})` }
    })
})

const debts = computed(() =>
  selectedCreditor.value == null
    ? []
    : documents.value.filter((document) => document.creditor === selectedCreditor.value)
)

watch(selectedCreditor, () => {
  selectedDebts.value = []
})

onMounted(() => {
  if (creditors.value.length === 1) selectedCreditor.value = creditors.value[0]?.id ?? null
})

// The candidate amount is the materialized debt, the same basis the backend plan is built on
// (ADA-4445): select and setup no longer show figures derived two different ways.
function label(debt: SettlementDocument) {
  const amount = formatCurrency(debt.total_remaining_transaction_amount ?? 0, debt.currency ?? 'SEK')
  return `${amount} - ${debt.reference_number ?? ''}`
}

async function next() {
  const payload = dialogsStore.amortization
  const multiple = payload?.multiple
  if (payload == null || multiple == null || loading.value) return
  const selected = documents.value.filter((document) => selectedDebts.value.includes(document._id))
  const first = selected[0]
  if (first == null) return

  loading.value = true
  try {
    // Any selected document works as the gate target: the selection is single-creditor and the
    // action params are creditor-level config, so every case yields the same params; the commit
    // POST carries ALL selected ids. Legacy used the first pick the same way (PR #895 review).
    const id = first.cases?.[0]?.case ?? first._id
    const { data, response } = await apiFetch.request('get', '/domain-actions/cases/new_amortization/{resource_id}', {
      params: { path: { resource_id: id } },
    })
    // The candidates were vetted eligible by HomeActions, so 200 is the norm; anything else
    // (eligibility changed meanwhile) just keeps the user on the select step.
    if (response.status !== 200) return
    dialogsStore.amortization = {
      ...payload,
      // The gate's 200 body is the action params (untyped in swagger — "Swagger lies").
      params: { ...(data as unknown as Omit<AmortizationParams, 'id'>), id },
      multiple: { ...multiple, selected: [...selectedDebts.value] },
    }
    emit('next')
  } catch {
    // apiFetch.request has no global error handler (unlike useQuery/useMutation), so swallow the
    // rejection here and keep the user on the select step (PR #895 review).
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <v-sheet class="d-flex flex-column">
    <amortization-back-toolbar page="select" />

    <section class="px-4">
      <v-select
        v-model="selectedCreditor"
        :items="creditors"
        :label="t('common.debts_from')"
        item-title="name"
        item-value="id"
        variant="outlined"
        hide-details
        class="mb-6"
      />
      <div
        v-if="selectedCreditor != null"
        class="text-body-large font-weight-bold mb-4"
      >
        {{ t('common.include_which_debts') }}
        {{ `(${selectedDebts.length}/${debts.length})` }}
      </div>
    </section>

    <section class="flex-grow-1 overflow-y-auto px-4">
      <v-checkbox
        v-for="debt in debts"
        :key="debt._id"
        v-model="selectedDebts"
        :label="label(debt)"
        :value="debt._id"
        hide-details
      />
    </section>

    <v-btn
      :disabled="selectedDebts.length === 0"
      :loading="loading"
      variant="outlined"
      class="ma-4"
      @click="next"
    >
      {{ t('common.move_on') }}
    </v-btn>
  </v-sheet>
</template>
